/**
 * Title-Case Exemption Normalizer (May 5, 2026)
 *
 * Normalizes the user-entered "Other (Custom)" exemption text to Title Case
 * before save. Predefined exemption labels (Homestead, Senior, etc.) are
 * never touched — this utility only runs against the `exemption_other`
 * sibling string.
 *
 * Examples (matching the spec verbatim):
 *   "HOMESTEAD"                   -> "Homestead"
 *   "disabled veteran exemption"  -> "Disabled Veteran Exemption"
 *   "senior exemption"            -> "Senior Exemption"
 *
 * Edge cases:
 *   - All-caps state codes / well-known acronyms are preserved (CA, NY, VA, US).
 *   - Whitespace is collapsed and trimmed.
 *   - Empty / null input returns "" (defensive).
 *
 * Mirrors the backend `_smart_title` behavior in `pdf_service.py` so the
 * displayed text on UI and PDF render to the same value.
 */

// State abbreviations + a small set of well-known acronyms common in
// exemption names. Kept tight to avoid false-positives on real words.
const PRESERVE_UPPER = new Set([
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
  // Acronyms
  'STAR','VA','US','USA','LLC','LLP','PLC','II','III','IV',
]);

const _titleCaseWord = (word) => {
  if (!word) return word;
  // If the original token is all-uppercase AND in the preserve set, keep it.
  // Strip leading/trailing parens/punctuation only for the comparison so
  // tokens like "(NY)" stay "(NY)".
  const stripped = word.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '');
  if (stripped && stripped === stripped.toUpperCase() && PRESERVE_UPPER.has(stripped)) {
    return word;  // preserve casing including any wrapping punctuation
  }
  // Standard title-case: first letter upper, rest lower.
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

/**
 * Normalize an `exemption_other` string to Title Case.
 * Returns "" for null/undefined/blank input.
 *
 * @param {string} text
 * @returns {string}
 */
export const titleCaseExemption = (text) => {
  if (text === null || text === undefined) return '';
  const trimmed = String(text).trim();
  if (!trimmed) return '';
  // Collapse internal whitespace
  return trimmed.split(/\s+/).map(_titleCaseWord).join(' ');
};

export default titleCaseExemption;
